import { debounce, preloadImage } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';

const dialogs = new WeakMap();
const observers = new WeakMap();
const scheduledEnhancements = new WeakMap();
const reducingMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

initGalleryLightboxes();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initGalleryLightboxes());
}

document.addEventListener('shopify:section:load', (event) => initGalleryLightboxes(event.target));
document.addEventListener('shopify:block:select', (event) => initGalleryLightboxes(event.target));
document.addEventListener('shopify:block:reorder', (event) => initGalleryLightboxes(event.target));

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('.gallery-block__lightbox-button') : null;
  if (!(trigger instanceof HTMLButtonElement)) return;

  const gallery = trigger.closest('.gallery-block');
  const item = trigger.closest('.gallery-block__item');
  if (!(gallery instanceof HTMLElement) || !(item instanceof HTMLElement)) return;

  event.preventDefault();

  enhanceGallery(gallery);

  const items = getLightboxItems(gallery);
  const index = items.indexOf(item);
  if (index < 0) return;

  openGalleryLightbox(gallery, index, trigger);
});

/**
 * @param {HTMLElement} gallery
 * @param {number} index
 * @param {HTMLButtonElement} trigger
 */
function openGalleryLightbox(gallery, index, trigger) {
  const lightbox = ensureDialog(gallery);
  const { dialog } = lightbox;
  lightbox.activeTrigger = trigger;
  hydrateDialog(lightbox);

  if (!dialog.open) dialog.showModal();

  selectImage(lightbox, index, 'auto');
}

/**
 * @param {ParentNode} root
 */
function initGalleryLightboxes(root = document) {
  const galleries = new Set(root.querySelectorAll?.('.gallery-block[data-gallery-lightbox-enabled="true"]') || []);

  if (root instanceof HTMLElement) {
    const gallery = root.matches('.gallery-block') ? root : root.closest('.gallery-block');
    if (gallery instanceof HTMLElement) {
      galleries.add(gallery);
    }
  }

  for (const gallery of galleries) {
    if (!(gallery instanceof HTMLElement)) continue;
    if (gallery.dataset.galleryLightboxEnabled !== 'true') continue;

    enhanceGallery(gallery);
    observeGallery(gallery);
  }
}

/**
 * @param {HTMLElement} gallery
 */
function enhanceGallery(gallery) {
  if (gallery.dataset.galleryLightboxEnabled !== 'true') {
    cleanupGallery(gallery);
    return;
  }

  const eligibleItems = getEligibleItems(gallery);

  Array.from(gallery.querySelectorAll('.gallery-block__item--lightbox')).forEach((item) => {
    if (!(item instanceof HTMLElement) || eligibleItems.includes(item)) return;

    item.classList.remove('gallery-block__item--lightbox');
    item.removeAttribute('data-gallery-lightbox-index');
    item.querySelectorAll('.gallery-block__lightbox-button').forEach((button) => button.remove());
  });

  eligibleItems.forEach((item, index) => {
    item.classList.add('gallery-block__item--lightbox');
    item.dataset.galleryLightboxIndex = `${index}`;
    item.dataset.galleryIndex = `${index}`;

    if (item.querySelector('.gallery-block__lightbox-button')) return;

    const image = item.querySelector('img.gallery-block__image');
    if (!(image instanceof HTMLImageElement)) return;

    item.append(createTriggerButton(item, image));
  });
}

/**
 * @param {HTMLElement} gallery
 */
function observeGallery(gallery) {
  if (observers.has(gallery)) return;

  const observer = new MutationObserver(() => scheduleEnhancement(gallery));
  observer.observe(gallery, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: [
      'class',
      'href',
      'src',
      'srcset',
      'data_max_resolution',
      'data-max-resolution',
      'data-gallery-lightbox-enabled',
    ],
  });

  observers.set(gallery, observer);
}

/**
 * @param {HTMLElement} gallery
 */
function scheduleEnhancement(gallery) {
  window.clearTimeout(scheduledEnhancements.get(gallery));
  scheduledEnhancements.set(
    gallery,
    window.setTimeout(() => {
      scheduledEnhancements.delete(gallery);
      enhanceGallery(gallery);
    }, 50)
  );
}

/**
 * @param {HTMLElement} item
 * @param {HTMLImageElement} image
 */
function createTriggerButton(item, image) {
  const button = document.createElement('button');
  const label = getImageLabel(item, image);
  const hiddenLabel = document.createElement('span');

  button.type = 'button';
  button.className = 'gallery-block__lightbox-button';
  button.setAttribute('aria-label', `Open ${label} in lightbox`);
  hiddenLabel.className = 'visually-hidden';
  hiddenLabel.textContent = `Open ${label} in lightbox`;
  button.append(hiddenLabel);

  return button;
}

/**
 * @param {HTMLElement} gallery
 */
function cleanupGallery(gallery) {
  gallery.querySelectorAll('.gallery-block__item--lightbox').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    item.classList.remove('gallery-block__item--lightbox');
    item.removeAttribute('data-gallery-lightbox-index');
    item.querySelectorAll('.gallery-block__lightbox-button').forEach((button) => button.remove());
  });
}

/**
 * @param {HTMLElement} gallery
 */
function ensureDialog(gallery) {
  const cached = dialogs.get(gallery);
  if (cached) return cached;

  const lightboxId = gallery.dataset.galleryLightboxId || `gallery-lightbox-${crypto.randomUUID()}`;
  const wrapper = document.createElement('gallery-lightbox');
  wrapper.className = 'gallery-lightbox';
  wrapper.id = lightboxId;

  const dialog = document.createElement('dialog');
  dialog.className = 'dialog-zoomed-gallery__dialog';
  dialog.setAttribute('scroll-lock', '');

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'button button-unstyled close-button dialog-zoomed-gallery__close-button';
  closeButton.setAttribute('aria-label', 'Close');
  closeButton.innerHTML = `
    <span class="visually-hidden">Close</span>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const thumbnailsContainer = document.createElement('div');
  thumbnailsContainer.className = 'dialog-thumbnails-list-container';

  const thumbnails = document.createElement('scroll-hint');
  thumbnails.className = 'dialog-thumbnails-list list-unstyled';
  thumbnailsContainer.append(thumbnails);

  const mediaList = document.createElement('ul');
  mediaList.className = 'dialog-zoomed-gallery gallery-lightbox__media-list list-unstyled';

  dialog.append(closeButton, thumbnailsContainer, mediaList);
  wrapper.append(dialog);
  gallery.append(wrapper);

  const lightbox = { dialog, thumbnails, mediaList, gallery };
  dialogs.set(gallery, lightbox);

  closeButton.addEventListener('click', () => closeDialog(lightbox));
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    closeDialog(lightbox);
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog(lightbox);
  });
  mediaList.addEventListener('scroll', debounce(() => syncActiveThumbnail(lightbox), 50));

  hydrateDialog(lightbox);
  return lightbox;
}

/**
 * @param {{ gallery: HTMLElement, thumbnails: HTMLElement, mediaList: HTMLElement }} lightbox
 */
function hydrateDialog(lightbox) {
  const items = getLightboxItems(lightbox.gallery);

  lightbox.thumbnails.replaceChildren();
  lightbox.mediaList.replaceChildren();

  for (const [index, item] of items.entries()) {
    const image = item.querySelector('img.gallery-block__image');
    if (!(image instanceof HTMLImageElement)) continue;

    if (items.length > 1) {
      lightbox.thumbnails.append(createThumbnail(image, index, lightbox));
    }

    lightbox.mediaList.append(createMediaItem(image, item, lightbox));
  }
}

/**
 * @param {HTMLElement} gallery
 */
function getLightboxItems(gallery) {
  return getEligibleItems(gallery);
}

/**
 * @param {HTMLElement} gallery
 */
function getEligibleItems(gallery) {
  return Array.from(gallery.querySelectorAll('.gallery-block__item')).filter((item) => {
    if (!(item instanceof HTMLElement)) return false;
    if (item.querySelector('.gallery-block__link')) return false;
    const image = item.querySelector('img.gallery-block__image');
    if (!(image instanceof HTMLImageElement)) return false;

    return Boolean(image.currentSrc || image.src || image.getAttribute('src'));
  });
}

/**
 * @param {HTMLImageElement} sourceImage
 * @param {number} index
 * @param {object} lightbox
 */
function createThumbnail(sourceImage, index, lightbox) {
  const thumbnail = document.createElement('button');
  thumbnail.type = 'button';
  thumbnail.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
  thumbnail.setAttribute('aria-label', `Scroll to ${sourceImage.alt || 'gallery image'}`);
  thumbnail.style.setProperty('--aspect-ratio', imageRatio(sourceImage));
  thumbnail.style.setProperty('--gallery-aspect-ratio', imageRatio(sourceImage));
  if (index === 0) thumbnail.setAttribute('aria-selected', 'true');

  const image = sourceImage.cloneNode(false);
  image.removeAttribute('srcset');
  image.removeAttribute('sizes');
  image.loading = 'lazy';

  thumbnail.append(image);
  thumbnail.addEventListener('click', () => selectImage(lightbox, index));
  thumbnail.addEventListener('pointerenter', () => preloadHighResolution(lightbox.mediaList.children[index]));

  return thumbnail;
}

/**
 * @param {HTMLImageElement} sourceImage
 * @param {Element} sourceItem
 * @param {{ dialog: HTMLDialogElement }} lightbox
 */
function createMediaItem(sourceImage, sourceItem, lightbox) {
  const mediaItem = document.createElement('li');
  mediaItem.className = 'product-media-container product-media-container--image product-media-container--zoomable gallery-lightbox__media-item';
  mediaItem.style.setProperty('--media-preview-ratio', imageRatio(sourceImage));

  const media = document.createElement('div');
  media.className = 'product-media gallery-lightbox__media';
  media.style.setProperty('--ratio', imageRatio(sourceImage));

  const image = sourceImage.cloneNode(false);
  image.className = 'product-media__image gallery-lightbox__image';
  image.loading = 'lazy';

  media.append(image);
  mediaItem.append(media);

  const caption = sourceItem.querySelector('.gallery-block__caption');
  if (caption?.textContent?.trim()) {
    const lightboxCaption = document.createElement('p');
    lightboxCaption.className = 'gallery-lightbox__caption';
    lightboxCaption.textContent = caption.textContent.trim();
    mediaItem.append(lightboxCaption);
  }

  mediaItem.addEventListener('click', () => closeDialog(lightbox));

  return mediaItem;
}

/**
 * @param {{ mediaList: HTMLElement, thumbnails: HTMLElement }} lightbox
 * @param {number} index
 * @param {ScrollBehavior} behavior
 */
function selectImage(lightbox, index, behavior = 'smooth') {
  const mediaItem = lightbox.mediaList.children[index];
  if (!(mediaItem instanceof HTMLElement)) return;

  mediaItem.scrollIntoView({
    behavior: reducingMotion.matches ? 'auto' : behavior,
    block: 'nearest',
    inline: 'nearest',
  });

  Array.from(lightbox.thumbnails.querySelectorAll('button')).forEach((thumbnail, thumbnailIndex) => {
    thumbnail.setAttribute('aria-selected', `${thumbnailIndex === index}`);
  });

  preloadHighResolution(mediaItem);
}

/**
 * @param {{ mediaList: HTMLElement, thumbnails: HTMLElement }} lightbox
 */
function syncActiveThumbnail(lightbox) {
  const mediaItems = Array.from(lightbox.mediaList.children).filter((item) => item instanceof HTMLElement);
  if (!mediaItems.length) return;

  const container = lightbox.mediaList.getBoundingClientRect();
  const targetX = container.left + container.width / 2;
  const targetY = container.top + container.height / 2;
  const activeIndex = mediaItems.reduce((bestIndex, item, index) => {
    const current = item.getBoundingClientRect();
    const best = mediaItems[bestIndex].getBoundingClientRect();
    const bestDistance =
      Math.abs(best.left + best.width / 2 - targetX) + Math.abs(best.top + best.height / 2 - targetY);
    const currentDistance =
      Math.abs(current.left + current.width / 2 - targetX) + Math.abs(current.top + current.height / 2 - targetY);

    return currentDistance < bestDistance ? index : bestIndex;
  }, 0);

  Array.from(lightbox.thumbnails.querySelectorAll('button')).forEach((thumbnail, index) => {
    thumbnail.setAttribute('aria-selected', `${index === activeIndex}`);
  });

  preloadHighResolution(mediaItems[activeIndex]);
}

/**
 * @param {Element | null | undefined} mediaItem
 */
function preloadHighResolution(mediaItem) {
  const image = mediaItem?.querySelector('img.product-media__image');
  if (!(image instanceof HTMLImageElement)) return;

  const highResolutionUrl = image.dataset.maxResolution || image.getAttribute('data_max_resolution');
  if (!highResolutionUrl || image.src === highResolutionUrl) return;

  preloadImage(highResolutionUrl);
  image.src = highResolutionUrl;
  image.removeAttribute('srcset');
}

/**
 * @param {{ dialog: HTMLDialogElement, activeTrigger?: HTMLButtonElement }} lightbox
 */
function closeDialog(lightbox) {
  if (!lightbox.dialog.open) return;

  lightbox.dialog.close();

  if (lightbox.activeTrigger?.isConnected) {
    lightbox.activeTrigger.focus({ preventScroll: true });
  }

  window.dispatchEvent(new DialogCloseEvent());
}

/**
 * @param {HTMLImageElement} image
 */
function imageRatio(image) {
  const width = Number(image.getAttribute('width')) || image.naturalWidth || 1;
  const height = Number(image.getAttribute('height')) || image.naturalHeight || 1;

  return `${width / height}`;
}

/**
 * @param {HTMLElement} item
 * @param {HTMLImageElement} image
 */
function getImageLabel(item, image) {
  const caption = item.querySelector('.gallery-block__caption')?.textContent?.trim();
  const alt = image.alt?.trim();

  return caption || alt || 'gallery image';
}
